import { GPS } from "../types";

const EARTH_RADIUS_M = 6371e3;

/**
 * How far a trap site's pin may sit from its geocoded address before we warn.
 *
 * A warning, never a validation error: a trap in a field behind a building, at
 * a park entrance, or on a levee is legitimately this far from the nearest
 * addressable point, and there is no coordinate input to "correct" it with.
 */
export const DRIFT_THRESHOLD_M = 1000;

const toRadians = (deg: number): number => (deg * Math.PI) / 180;

/** Great-circle distance in metres. */
export const haversineMeters = (a: GPS, b: GPS): number => {
  const phi1 = toRadians(a.lat);
  const phi2 = toRadians(b.lat);
  const deltaPhi = toRadians(b.lat - a.lat);
  const deltaLambda = toRadians(b.lon - a.lon);

  const h =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) *
      Math.cos(phi2) *
      Math.sin(deltaLambda / 2) *
      Math.sin(deltaLambda / 2);

  return Math.round(
    EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h)),
  );
};

/**
 * Leaflet says {lat, lng}; our API says {lat, lon}; ArcGIS says {x, y}. Three
 * orderings in one call path, and a silent swap produces coordinates that look
 * plausible but sit in the wrong hemisphere - so every crossing goes through
 * these two rather than being destructured at the call site.
 */
export const toLatLng = (gps: GPS): [number, number] => [gps.lat, gps.lon];

export const fromLatLng = (latlng: { lat: number; lng: number }): GPS => ({
  lat: latlng.lat,
  lon: wrapLongitude(latlng.lng),
});

/**
 * Normalise a longitude to [-180, 180).
 *
 * worldCopyJump is on, so panning past the antimeridian hands back values like
 * 361. The in-range short-circuit is load-bearing rather than an optimisation:
 * running an ordinary longitude through the modulo introduces floating-point
 * error (-76.6425 becomes -76.64249999999998), which would make the editor
 * read as dirty after a round trip that changed nothing.
 */
const wrapLongitude = (lng: number): number =>
  lng >= -180 && lng < 180 ? lng : ((((lng + 180) % 360) + 360) % 360) - 180;

const FEET_PER_METER = 3.280839895;
const METERS_PER_MILE = 1609.344;

/**
 * Whether to show distances in feet and miles.
 *
 * Read from the browser's own locale rather than anything we store: the three
 * countries that use imperial for road distances are the US, Liberia and
 * Myanmar. Everything is stored in metres regardless; this only decides how it
 * is read back.
 */
export const usesImperial = (
  locales: readonly string[] = typeof navigator !== "undefined"
    ? navigator.languages || [navigator.language]
    : [],
): boolean => {
  for (const tag of locales) {
    if (!tag) continue;
    // Region subtag, e.g. "en-US" -> "US". Falls through to the next locale
    // when a tag carries no region at all ("en"), rather than guessing.
    const region = tag.split("-")[1]?.toUpperCase();
    if (!region || region.length !== 2) continue;
    return ["US", "LR", "MM"].includes(region);
  }
  return false;
};

/** Metres rendered for reading, in whichever system the viewer expects. */
export const formatDistance = (
  meters: number,
  imperial: boolean = usesImperial(),
): string => {
  if (imperial) {
    const feet = meters * FEET_PER_METER;
    // Switch to miles at a tenth of one, where feet stop being readable.
    return feet < 528
      ? `${Math.round(feet)} ft`
      : `${(meters / METERS_PER_MILE).toFixed(1)} mi`;
  }
  return meters < 1000
    ? `${Math.round(meters)} m`
    : `${(meters / 1000).toFixed(1)} km`;
};

/** The units a radius may be expressed in. */
export const DISTANCE_UNITS = ["m", "km", "ft", "mi"] as const;
export type DistanceUnit = (typeof DISTANCE_UNITS)[number];

const METERS_PER_UNIT: Record<DistanceUnit, number> = {
  m: 1,
  km: 1000,
  ft: 0.3048,
  mi: 1609.344,
};

/** Units that are typically fractional, and so keep decimal places. */
const FRACTIONAL_UNITS: readonly DistanceUnit[] = ["km", "mi"];

export const distanceToMeters = (value: number, unit: DistanceUnit): number =>
  value * METERS_PER_UNIT[unit];

/**
 * The same distance expressed in another unit, rounded for a form field.
 *
 * Whole numbers for metres and feet, two decimals for kilometres and miles -
 * a radius of "0.62 mi" is useful, "0.6213711922 mi" is not.
 */
export const convertDistance = (
  value: number,
  from: DistanceUnit,
  to: DistanceUnit,
): number => {
  if (from === to) return value;
  const converted = distanceToMeters(value, from) / METERS_PER_UNIT[to];
  return FRACTIONAL_UNITS.includes(to)
    ? Math.round(converted * 100) / 100
    : Math.round(converted);
};

/** Whether a unit should accept decimals in an input. */
export const unitAllowsDecimals = (unit: DistanceUnit): boolean =>
  FRACTIONAL_UNITS.includes(unit);

/**
 * The unit a radius should default to.
 *
 * Only the default: the user picks freely afterwards, and whatever they pick
 * is stored alongside the number rather than being normalised away.
 */
export const defaultDistanceUnit = (
  imperial: boolean = usesImperial(),
): DistanceUnit => (imperial ? "mi" : "km");

/**
 * The radius of a location that claims no ground at all.
 *
 * A sentinel rather than 0 or null: the field is optional in the record, so a
 * missing radius has to read back as *something*, and 0 is a number the form
 * can produce by accident. -1 is the one value a distance can never be, which
 * makes "no boundary" explicit on the wire. Matches the backend's NO_RADIUS.
 */
export const NO_RADIUS = -1;

/**
 * Whether a radius describes an actual boundary.
 *
 * Absent and NO_RADIUS answer the same: a location that never set a radius
 * claims no ground, rather than standing in a default circle nobody drew. No
 * boundary is drawn and no distance is warned about for a location this says
 * no about - the number is the only thing that decides it, so the check has
 * one home rather than being re-derived at every call site.
 */
export const hasBoundary = (value?: number | null): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;
